'use client';

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { User, HelpCircle } from "lucide-react";

const IMAGE_CDN =
  process.env.NEXT_PUBLIC_IMAGE_CDN_URL || "";

type HeaderVariant = "transparent" | "fixed";

interface HeaderProps {
  variant?: HeaderVariant;
}

export default function Header({ variant = "transparent" }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (variant !== "transparent") return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [variant]);

  const headerClasses =
    variant === "transparent"
      ? `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white shadow-[0_0_18px_-2px_rgba(0,0,0,0.12)]"
            : "bg-transparent"
        }`
      : "fixed top-0 left-0 right-0 z-50 bg-white shadow-[0_0_18px_-2px_rgba(0,0,0,0.12)]";

  const textClasses =
    variant === "transparent" && !isScrolled ? "text-white" : "text-gray-800";

  const showWhiteLogo = variant === "transparent" && !isScrolled;

  return (
    <header className={headerClasses}>
      <div className="container mx-auto px-4">
        <div
          className={`flex items-center justify-between transition-all duration-300 ease-in-out ${
            isScrolled || variant === "fixed" ? "h-20" : "h-[90px]"
          }`}
        >
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-3 group">
            <div className="transition-transform duration-300">
              <Image
                src={
                  showWhiteLogo
                    ? `${IMAGE_CDN}/assets/img/deokar-maratha-vadhu-var-suchak-logo-white.svg`
                    : `${IMAGE_CDN}/assets/img/deokar-maratha-vadhu-var-suchak-logo.svg`
                }
                alt="Deokar Maratha Vadhu Var Suchak Logo"
                width={162}
                height={75}
                className={`header-logo w-auto transition-all duration-300 ${
                  isScrolled || variant === "fixed" ? "h-[55px]" : "h-[75px]"
                }`}
                priority
              />
            </div>
          </Link>

          {/* Right side links with icons */}
          <div className="flex items-center space-x-6">
            <Link
              href="https://marathivadhuvarsuchak.com/login"
              className={`group flex items-center space-x-2 hover:text-primary-400 transition-all duration-300 ${textClasses}`}
            >
              <div className="p-1 rounded-lg bg-white/10 group-hover:bg-primary-400/20 transition-colors">
                <User size={20} />
              </div>
              <span className="font-normal">Login</span>
            </Link>
            <Link
              href="https://marathivadhuvarsuchak.com/help"
              className={`group flex items-center space-x-2 hover:text-primary-400 transition-all duration-300 ${textClasses}`}
            >
              <div className="p-1 rounded-lg bg-white/10 group-hover:bg-primary-400/20 transition-colors">
                <HelpCircle size={20} />
              </div>
              <span className="font-normal">Help</span>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

